import { useQuery } from "@tanstack/react-query";
import { rpc } from "@vixio/data";
import { Callout, DataTable, KpiRow, KpiTile, Meter, Skeleton, formatInt } from "@vixio/ui";
import type { Aggregate } from "./api";

function fmt1(v: number | null | undefined): string {
  return v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
function fmt2(v: number | null | undefined): string {
  return v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function useAggregate(formId: string | null | undefined, groupKey?: string | null) {
  return useQuery({
    queryKey: ["forms", "aggregate", formId, groupKey ?? null],
    enabled: Boolean(formId),
    queryFn: () => rpc("forms", "aggregate", { p_form_id: formId!, p_group_key: groupKey ?? null }),
  });
}

/**
 * Agregado do formulário vindo de `forms.aggregate`: o banco decide o k-anonimato. Abaixo do mínimo,
 * só aparece o aviso; nenhuma média parcial é calculada na interface.
 */
export function AggregateView({ formId, scaleLabel, groupKey }: { formId: string; scaleLabel?: string; groupKey?: string | null }) {
  const q = useAggregate(formId, groupKey);
  if (q.isLoading) return <Skeleton lines={4} />;
  if (q.error) return <Callout tone="crit" title="Não foi possível carregar o agregado">{String((q.error as Error).message)}</Callout>;
  const a = q.data as Aggregate | undefined;
  if (!a) return null;
  if (a.suppressed) {
    return (
      <Callout tone="info" title="Agregado protegido">
        O banco só libera médias com pelo menos {a.min_group_size} respostas (k-anonimato). Nada é calculado na interface antes disso.
      </Callout>
    );
  }
  const scored = a.items.filter((i) => i.n > 0);
  return (
    <div className="vx-stack vx-stack--lg">
      <KpiRow>
        <KpiTile label="Respostas no agregado" value={formatInt(a.n)} foot={`mínimo k = ${a.min_group_size}`} />
        <KpiTile label="Pontuação média" value={a.score === null ? "-" : `${fmt1(a.score)}/100`} foot="média das pontuações gerais" />
        <KpiTile label="Dimensões" value={formatInt(a.dimensions.length)} />
      </KpiRow>
      {a.dimensions.length > 0 ? (
        <div className="vx-stack" role="list" aria-label="Pontuação por dimensão">
          {a.dimensions.map((d) => (
            <div key={d.key} role="listitem" className="vx-row" style={{ flexWrap: "nowrap", gap: 12 }}>
              <span style={{ width: 200, minWidth: 0, flexShrink: 0 }} className="text-2">
                {d.label}
              </span>
              <span style={{ flex: 1, minWidth: 60 }}>
                <Meter pct={d.score ?? 0} />
              </span>
              <span className="num" style={{ width: 56, textAlign: "right" }}>
                {fmt1(d.score)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {scored.length > 0 ? (
        <DataTable
          caption="Média por item"
          data={a.items}
          rowKey={(i) => i.id}
          bare
          exportName="agregado-itens"
          columns={[
            { key: "id", header: "Item", cell: (i) => <span className="mono">{i.id}</span> },
            { key: "text", header: "Afirmação" },
            { key: "mean", header: scaleLabel ? `Média (${scaleLabel})` : "Média", align: "right", cell: (i) => <span className="num">{fmt2(i.mean)}</span>, csv: (i) => i.mean },
            { key: "n", header: "n", kind: "number" },
          ]}
        />
      ) : null}
    </div>
  );
}
